import React, { useEffect, useRef, useState } from 'react'
import { copyToClipboard } from '../../../utils/link.helper'
import { Button, Flex } from 'antd'
import { CopyOutlined, UploadOutlined } from '@ant-design/icons'
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/material.css'
import 'codemirror/addon/edit/closebrackets'
import 'codemirror/addon/edit/matchbrackets'
import { Controlled as ControlledEditor } from 'react-codemirror2'
import './editor.scss'
import PropTypes from 'prop-types'
import jBox from 'jbox'
import {
    buildPatternGutter,
    getStringCoordinates,
    importLanguagePackage,
    incrementTermsLine,
    isTermValid,
    mapFormatToMIME,
    removeIntersections,
} from './codemirror.helper'
import { getContrastColor } from '../../../utils/colors.helper'
import { smoothScroll } from '../../../utils/dom.helper'
import { getFileContent } from '../../../utils/file.helper'
import FileUpload from '../FileUpload'
import { LANGUAGES } from '../../../constants/languages'
import Title from 'antd/es/typography/Title'
import { isNil, isNumber, words } from 'lodash'
import QuillRenderer from '../text-editor/QuillRenderer'
import Text from 'antd/es/typography/Text'
import { errorColor } from '../../../constants/styles'
import { useExcessWordsHighlights, useHighlights } from './hooks'

const Editor = ({
    className,
    language,
    value: inputValue,
    onChange,
    readOnly,
    title,
    patternsList,
    gutterUpdateTrigger,
    onGutterClick,
    selectedTerms: initialSelectedTerms,
    onTermSelect,
    selectedPatterns: initialSelectedPatterns,
    onPatternSelect,
    selectedSnippets: initialSelectedSnippets,
    onSnippetSelect,
    singleSnippetSelectionMode = false,
    patternFeedback,
    showActions = true,
    extraActions,
    wrapLines = false,
    showLineNumbers = true,
    wrapperStyles,
    showWordsCount = false,
    wordsLimit,
    highlightRanges,
    footer,
}) => {
    const value = typeof inputValue === 'string' ? inputValue.replace(/\u00A0/g, ' ') : (inputValue ?? '')
    const editorInstance = useRef(null)
    const screenReaderMessageRef = useRef(null)

    const selectedTermsRef = useRef([]) //copying state to ref because codemirror doesn't update event listener and eventually using initial state. More: https://stackoverflow.com/questions/55265255/react-usestate-hook-event-handler-using-initial-state
    const setSelectedTermsRef = value => {
        selectedTermsRef.current = value
    }

    const [selectedPatterns, setSelectedPatterns] = useState([]) //maybe use initial state from props

    const updateSelectedTerms = (newSelectedTerms, editor = editorInstance.current) => {
        editor.getAllMarks().forEach(marker => marker.clear())
        const selectedTermInstances = newSelectedTerms.map(selectedTerm => {
            const mark = editor.markText(
                { line: selectedTerm.cords.anchor.line, ch: selectedTerm.cords.anchor.ch },
                { line: selectedTerm.cords.head.line, ch: selectedTerm.cords.head.ch },
                { className: 'marked-text' },
            )

            return {
                ...selectedTerm,
                mark,
            }
        })
        setSelectedTermsRef(selectedTermInstances)
        editor.refresh()
    }

    const selectedSnippetsRef = useRef([]) //copying state to ref because codemirror doesn't update event listener and eventually using initial state. More: https://stackoverflow.com/questions/55265255/react-usestate-hook-event-handler-using-initial-state
    const setSelectedSnippetsRef = value => {
        selectedSnippetsRef.current = value
    }

    const updateSelectedSnippets = (selectedSnippets, editor = editorInstance.current) => {
        if (selectedSnippetsRef.current.length) {
            for (let i = 0; i <= editor.lastLine(); i++) {
                const lineInstance = editor.getLineHandle(i)
                if (lineInstance) {
                    editor.removeLineClass(lineInstance, 'background', 'marked-line')
                    editor.removeLineClass(lineInstance, 'background', 'marked-line-top')
                    editor.removeLineClass(lineInstance, 'background', 'marked-line-bottom')
                }
            }
        }

        if (!selectedSnippets.length && selectedSnippetsRef.current.length) {
            //to handle case when removing the last snippet
            setSelectedSnippetsRef([])
        } else if (selectedSnippets.length) {
            selectedSnippets.map(selectedSnippet => {
                const { start, end } = selectedSnippet
                for (let i = start; i <= end; i++) {
                    const lineInstance = editor.getLineHandle(i)
                    if (lineInstance) {
                        editor.addLineClass(lineInstance, 'background', 'marked-line')
                        if (i === start) editor.addLineClass(lineInstance, 'background', 'marked-line-top')
                        if (i === end) editor.addLineClass(lineInstance, 'background', 'marked-line-bottom')
                    }
                }
            })
            setSelectedSnippetsRef(selectedSnippets)
        }
    }

    const formattedLanguage = mapFormatToMIME(language)
    importLanguagePackage(formattedLanguage)

    const handleChange = (editor, data, value) => {
        onChange(value)
    }

    const wordsCount = words(value).length
    const wordsLimitExceeded = isNumber(wordsLimit) && wordsCount > wordsLimit

    const applyWrapperStyles = editor => {
        if (editor && wrapperStyles) {
            const wrapperElement = editor.getWrapperElement()
            Object.keys(wrapperStyles).forEach(key => {
                wrapperElement.style[key] = wrapperStyles[key]
            })
        }
    }

    const editorDidMountFn = editor => {
        editorInstance.current = editor
        applyWrapperStyles(editor)

        // Force refresh to ensure all content is visible initially
        setTimeout(() => editor.refresh(), 0)
    }

    useHighlights(editorInstance, highlightRanges)
    useExcessWordsHighlights(editorInstance, value, wordsLimit)

    let classNamesForGutters = []
    if (patternsList) {
        classNamesForGutters = [
            ...new Set(
                patternsList.map(pattern => {
                    return `pattern-${pattern.motifId}`
                }),
            ),
        ]
    }

    const renderPatternFeedbackAsBlock = language === LANGUAGES.TEXT.value

    useEffect(() => {
        patternsList?.map(pattern => {
            if (isNil(pattern.startRow) || isNil(pattern.endRow)) return
            const isSelected = Boolean(findTheSameSelectedPattern(pattern))
            editorInstance.current.setGutterMarker(
                pattern.startRow - 1, // '-1' because counting in codemirror starts from 0
                `pattern-${pattern.motifId}`,
                buildPatternGutter(
                    editorInstance.current,
                    pattern.color,
                    pattern.motifId,
                    pattern.startRow,
                    pattern.endRow,
                    pattern.hideId,
                    pattern.tooltip,
                    onGutterClick,
                    isSelected,
                    onPatternSelect ? handleSelectPattern : null,
                ),
            )
        })

        let patternFeedbackBox
        let isMounted = true

        if (patternFeedback && !renderPatternFeedbackAsBlock) {
            const editorWidth = editorInstance.current.getWrapperElement().getBoundingClientRect().width
            const editorHeight = editorInstance.current.getWrapperElement().getBoundingClientRect().height
            const patternFeedbackBoxWidth = 300

            const patternGutter = document.querySelector(`.gutter-marker`)
            smoothScroll(patternGutter, {
                block: 'center',
                inline: 'start',
                behavior: 'smooth',
            }).then(() => {
                if (isMounted) {
                    const pointsString = patternFeedback.grade
                        ? `${patternFeedback.grade > 0 ? '+' : ''}${patternFeedback.grade} points`
                        : ''

                    patternFeedbackBox = new jBox('Tooltip', {
                        position: { x: 'right', y: 'center' },
                        offset: { x: editorWidth - patternFeedbackBoxWidth - 36 },
                        maxWidth: 300,
                        minHeight: 24,
                        maxHeight: '50vh',
                        outside: 'x',
                        pointer: 'left',
                        animation: 'move',
                        fixed: true,
                        draggable: true,
                        adjustPosition: true,
                        adjustTracker: true,
                        zIndex: 99999,
                        closeOnEsc: true,
                        title: patternFeedback.name ? `<b>${patternFeedback.name}</b>` : '',
                        content: patternFeedback.feedback,
                        footer: patternFeedback.grade ? `<code>${pointsString}</code>` : '',
                    })
                    patternFeedbackBox.open({ target: patternGutter })

                    const jBoxContainerElement = document.querySelector('.jBox-container') //jbox doesn't provide api to stylize container so doing it manually
                    jBoxContainerElement.style.backgroundColor = patternFeedback.color
                    jBoxContainerElement.style.color = getContrastColor(patternFeedback.color)
                    jBoxContainerElement.style.minHeight = 0

                    if (screenReaderMessageRef?.current)
                        screenReaderMessageRef.current.innerText = `${patternFeedback.feedback} - ${pointsString} - lines from ${patternFeedback.startRow} to ${patternFeedback.endRow}`
                }
            })
        }

        return () => {
            patternsList?.map(pattern => editorInstance.current.clearGutter(`pattern-${pattern.motifId}`))
            if (patternFeedbackBox) patternFeedbackBox.destroy()
            if (screenReaderMessageRef?.current) screenReaderMessageRef.current.innerText = ''
            isMounted = false
        }
    }, [gutterUpdateTrigger, patternsList, selectedPatterns])

    useEffect(() => {
        const handleKeyUp = event => {
            if (event.key === 'm' && event.ctrlKey) {
                editorInstance.current.addKeyMap({ Tab: false })
            }
        }

        const handleMouseUp = event => {
            if (event.button !== 0) return
            if (event.altKey) {
                handleSelectTerm(editorInstance.current)
            } else {
                handleSelectSnippet(editorInstance.current, event)
            }
        }

        document.addEventListener('keyup', handleKeyUp, true)
        editorInstance.current
            .getWrapperElement()
            .getElementsByClassName('CodeMirror-lines')[0]
            .addEventListener('mouseup', handleMouseUp, true)

        return () => {
            document.removeEventListener('keyup', handleKeyUp, true)
            editorInstance.current
                .getWrapperElement()
                .getElementsByClassName('CodeMirror-lines')[0]
                .removeEventListener('mouseup', handleMouseUp, true)
        }
    }, [onSnippetSelect])

    useEffect(() => {
        if (initialSelectedSnippets) {
            updateSelectedSnippets(
                initialSelectedSnippets.map(snippet => ({
                    ...snippet,
                    start: snippet.start - 1,
                    end: snippet.end - 1,
                })),
            )
        }
    }, [initialSelectedSnippets])

    useEffect(() => {
        if (initialSelectedTerms) {
            updateSelectedTerms(incrementTermsLine(initialSelectedTerms, -1))
        }
    }, [initialSelectedTerms])

    const handleSelectTerm = editor => {
        if (onTermSelect) {
            const { line, ch } = editor.getCursor()
            let { anchor, head } = editor.findWordAt({ line: line, ch: ch })
            const type = editor.getTokenTypeAt({ line, ch })

            if (type === 'string') {
                //if the word is a part of a string - to the request of SN-1793 we want to select the whole string as a term
                const stringCoordinates = getStringCoordinates(editor.getValue(), { line, ch })
                anchor = { line: stringCoordinates.anchor.line, ch: stringCoordinates.anchor.ch }
                head = { line: stringCoordinates.head.line, ch: stringCoordinates.head.ch }
            }

            const text = editor.getRange({ line: anchor.line, ch: anchor.ch }, { line: head.line, ch: head.ch })
            if (!isTermValid(text)) return
            const theSameTerm = selectedTermsRef.current.find(selectedTerm => {
                return (
                    selectedTerm.cords.anchor.line === anchor.line &&
                    selectedTerm.cords.anchor.ch === anchor.ch &&
                    selectedTerm.cords.head.line === head.line &&
                    selectedTerm.cords.head.ch === head.ch
                )
            })

            let pendingSelectedTermsState

            if (theSameTerm) {
                pendingSelectedTermsState = [
                    ...selectedTermsRef.current.filter(selectedTerm => selectedTerm !== theSameTerm),
                ]
            } else {
                pendingSelectedTermsState = [
                    ...selectedTermsRef.current,
                    {
                        text,
                        type,
                        cords: {
                            anchor: { line: anchor.line, ch: anchor.ch },
                            head: { line: head.line, ch: head.ch },
                        },
                    },
                ]
            }
            const response = onTermSelect(incrementTermsLine(pendingSelectedTermsState, 1))

            if (response) {
                updateSelectedTerms(incrementTermsLine(response, -1))
            } else {
                updateSelectedTerms(pendingSelectedTermsState)
            }
        }
    }

    const handleSelectPattern = selectedPattern => {
        if (onPatternSelect) {
            setSelectedPatterns(prevSelectedPatterns => {
                const theSamePattern = findTheSameSelectedPattern(selectedPattern, prevSelectedPatterns)
                let newSelectedPatterns

                if (theSamePattern) {
                    newSelectedPatterns = [
                        ...prevSelectedPatterns.filter(selectedPattern => selectedPattern !== theSamePattern),
                    ]
                } else {
                    newSelectedPatterns = [...prevSelectedPatterns, selectedPattern]
                }
                onPatternSelect(newSelectedPatterns)
                return newSelectedPatterns
            })
        }
    }

    const findTheSameSelectedPattern = (pattern, inputSelectedPatterns = selectedPatterns) =>
        inputSelectedPatterns.find(
            previouslySelectedPattern =>
                previouslySelectedPattern.motifId === pattern.motifId &&
                previouslySelectedPattern.startRow === pattern.startRow &&
                previouslySelectedPattern.endRow === pattern.endRow,
        )

    const handleSelectSnippet = (editor, event) => {
        if (onSnippetSelect) {
            const { line, ch } = editor.getCursor()

            if (event.shiftKey) editorInstance.current.setSelections([{ anchor: { ch, line }, head: { ch, line } }]) //to remove selection that was caused by holding shift when selecting multiline

            const selectionHasNotStartedYet = selectedSnippetsRef.current.length === 0

            let pendingSelectedSnippetsState = [...selectedSnippetsRef.current]

            if (selectionHasNotStartedYet) {
                pendingSelectedSnippetsState.push({ start: line, end: line })
            } else {
                const selectedSnippetsCleanedFromLineIntersections = selectedSnippetsRef.current.filter(
                    snippet => line < snippet.start || line > snippet.end,
                )

                if (selectedSnippetsCleanedFromLineIntersections.length !== selectedSnippetsRef.current.length) {
                    pendingSelectedSnippetsState = selectedSnippetsCleanedFromLineIntersections
                } else if (event.shiftKey) {
                    const lastSelectedSnippet = pendingSelectedSnippetsState[pendingSelectedSnippetsState.length - 1]

                    if (lastSelectedSnippet.start === lastSelectedSnippet.end) {
                        if (line < lastSelectedSnippet.start) {
                            pendingSelectedSnippetsState[pendingSelectedSnippetsState.length - 1] = {
                                start: line,
                                end: lastSelectedSnippet.start,
                            }
                        } else if (line > lastSelectedSnippet.start) {
                            pendingSelectedSnippetsState[pendingSelectedSnippetsState.length - 1] = {
                                start: lastSelectedSnippet.start,
                                end: line,
                            }
                        }
                    } else {
                        if (line < lastSelectedSnippet.start) {
                            pendingSelectedSnippetsState[pendingSelectedSnippetsState.length - 1] = {
                                start: line,
                                end: lastSelectedSnippet.end,
                            }
                        } else if (line > lastSelectedSnippet.end) {
                            pendingSelectedSnippetsState[pendingSelectedSnippetsState.length - 1] = {
                                start: lastSelectedSnippet.start,
                                end: line,
                            }
                        }
                    }
                } else {
                    pendingSelectedSnippetsState.push({ start: line, end: line })
                }

                pendingSelectedSnippetsState = removeIntersections(pendingSelectedSnippetsState)
                if (singleSnippetSelectionMode)
                    pendingSelectedSnippetsState = pendingSelectedSnippetsState.length
                        ? [pendingSelectedSnippetsState[pendingSelectedSnippetsState.length - 1]]
                        : []
            }

            const response = onSnippetSelect(
                pendingSelectedSnippetsState.map(snippet => ({
                    ...snippet,
                    start: snippet.start + 1,
                    end: snippet.end + 1,
                })),
            )

            if (response) {
                updateSelectedSnippets(
                    response.map(snippet => ({
                        ...snippet,
                        start: snippet.start - 1,
                        end: snippet.end - 1,
                    })),
                )
            } else {
                updateSelectedSnippets(pendingSelectedSnippetsState)
            }
        }
    }

    const handleKeyUp = (editor, event) => {
        if (event.key === 'Enter' && event.altKey) {
            handleSelectTerm(editor, event)
        } else if (event.key === 'Enter') {
            handleSelectSnippet(editor, event)
        }
    }

    const editor = (
        <ControlledEditor
            className='editor'
            onBeforeChange={handleChange}
            value={value}
            editorDidMount={editorDidMountFn}
            onKeyUp={handleKeyUp}
            options={{
                lint: true,
                mode: formattedLanguage,
                lineNumbers: showLineNumbers,
                autoRefresh: true,
                autocorrect: true,
                indentUnit: 4,
                gutters: [...classNamesForGutters, 'CodeMirror-linenumbers'],
                readOnly: readOnly,
                screenReaderLabel: 'solution',
                autoCloseBrackets: true,
                matchBrackets: true,
                lineWrapping: wrapLines || [LANGUAGES.TEXT.value, LANGUAGES.NEW_TEXT.value].includes(language),
            }}
        />
    )

    const patternFeedbackElement = patternFeedback && renderPatternFeedbackAsBlock && (
        <div
            style={{
                padding: 8,
                marginTop: 8,
                borderRadius: 6,
                backgroundColor: patternFeedback.color,
                color: getContrastColor(patternFeedback.color),
            }}
        >
            {patternFeedback.name && (
                <Title level={3} style={{ margin: '0 0 8px 0', fontSize: '1rem', color: 'inherit' }}>
                    {patternFeedback.name}
                </Title>
            )}
            <QuillRenderer htmlContent={patternFeedback.feedback} />
            {patternFeedback.grade ? (
                <div style={{ marginTop: 8 }}>
                    <code>
                        {patternFeedback.grade
                            ? `${patternFeedback.grade > 0 ? '+' : ''}${patternFeedback.grade} points`
                            : ''}
                    </code>
                </div>
            ) : null}
        </div>
    )

    const actions = showActions && (
        <div className='editor-actions'>
            {Array.isArray(extraActions) &&
                extraActions.map((element, index) => <React.Fragment key={index}>{element}</React.Fragment>)}
            {!readOnly && (
                <FileUpload
                    aria-label='import file'
                    onFileChange={file => getFileContent(file, fileContent => onChange(fileContent))}
                    accept={'.' + language}
                >
                    <Button type='text' icon={<UploadOutlined aria-hidden='true' />} title='Import file' />
                </FileUpload>
            )}
            <Button
                type='text'
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(value)}
                title='Copy solution to the clipboard'
            />
        </div>
    )

    return (
        <div className={`editor-container ${className || ''}`}>
            <div className='editor-header'>
                <div className='editor-title'>{title}</div>
                {actions}
            </div>
            {/*note: this div shouldn't be put below the editor, because it may create empty space*/}
            <div aria-live='assertive'>
                <p id='editor-screen-reader-message' ref={screenReaderMessageRef}></p>
            </div>
            {editor}
            <Flex justify={footer ? 'space-between' : 'end'} className='f-width' style={{ marginTop: footer ? 8 : 0 }}>
                {footer}
                {showWordsCount && (
                    <Text type='secondary' style={{ color: wordsLimitExceeded ? errorColor : undefined }}>
                        Words {wordsCount}
                        {wordsLimit ? ` / ${wordsLimit}` : ''} {wordsLimitExceeded ? '(limit exceeded)' : ''}
                    </Text>
                )}
            </Flex>
            {patternFeedbackElement}
        </div>
    )
}

Editor.propTypes = {
    className: PropTypes.string,
    language: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    readOnly: PropTypes.bool,
    title: PropTypes.element,
    patternsList: PropTypes.arrayOf(
        PropTypes.shape({
            startRow: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            endRow: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            motifId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            grade: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            color: PropTypes.string,
            hideId: PropTypes.bool,
            tooltip: PropTypes.string,
        }),
    ),
    gutterUpdateTrigger: PropTypes.any,
    onGutterClick: PropTypes.func, //params: (motifId: number, startRow: number, endRow: number): void
    selectedTerms: PropTypes.arrayOf(PropTypes.object), //SelectedTermObj
    onTermSelect: PropTypes.func, //params: (selectedTerms: SelectedTermObj[]): SelectedTermObj[]
    onPatternSelect: PropTypes.func, //params: (selectedPatterns: PatternObj[]): void //note that this can't be used with onGutterClick
    selectedSnippets: PropTypes.arrayOf(
        PropTypes.shape({
            start: PropTypes.oneOfType([PropTypes.number, PropTypes.instanceOf(null)]),
            end: PropTypes.oneOfType([PropTypes.number, PropTypes.instanceOf(null)]),
            //isValid: PropTypes.bool - not implemented
        }),
    ),
    onSnippetSelect: PropTypes.func, //params: (selectedSnippets: SnippetObj[]): SnippetObj[]
    singleSnippetSelectionMode: PropTypes.bool,
    patternFeedback: PropTypes.shape({
        name: PropTypes.string,
        feedback: PropTypes.string,
        grade: PropTypes.number,
        color: PropTypes.string,
    }),
    showPatternId: PropTypes.bool,
    showActions: PropTypes.bool,
    extraActions: PropTypes.arrayOf(PropTypes.element),
    wrapLines: PropTypes.bool,
    showLineNumbers: PropTypes.bool,
    wrapperStyles: PropTypes.object,
    showWordsCount: PropTypes.bool,
    wordsLimit: PropTypes.number,
    highlightRanges: PropTypes.arrayOf(
        PropTypes.shape({
            start: PropTypes.number.isRequired,
            end: PropTypes.number.isRequired,
            opacity: PropTypes.number,
        }),
    ),
}

export default Editor
